import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    model,
    OnDestroy,
    output,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import type { Core as Cytoscape } from 'cytoscape';
import { debounceTime, firstValueFrom, fromEvent, Subscription } from 'rxjs';

import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { SynopticNodeData } from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.model';
import {
    Cables,
    Cassette,
    Cassettes,
    Drawer,
    SelectedNode,
    SynopticFilterOption,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { SynopticBayPlanEditComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-bay-plan-edit/synoptic-bay-plan-edit.component';
import { SynopticPlanEditComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/synoptic-plan-edit.component';
import { SynopticPlanHeaderComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-header/synoptic-plan-header.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    GenericSynopticItem,
    GraceThdSynopticItem,
    Layer,
    PlanTypeEnum,
    SynopticBayPlanItem,
    SynopticBoxPlanItem,
    SynopticService,
} from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { SynopticMapData } from '@connect-services/client/services/broadcast-channel.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { BoxPlanEditModeStateService } from '@connect-services/client/services/synoptic/edit/box-plan-edit-mode-state.service';
import { DepositWithStandard } from '@connect-services/client/services/synoptic/export-synoptic-cartouche.service';
import { MapSynopticRedirectionService } from '@connect-services/client/services/synoptic/map-synoptic-redirection.service';
import {
    SelectedSynoptic,
    SelectSynopticService,
} from '@connect-services/client/services/synoptic/select-synoptic.service';
import { SynopticGraphService } from '@connect-services/client/services/synoptic/synoptic-graph.service';
import { SynopticPlanBuilderService } from '@connect-services/client/services/synoptic/synoptic-plan-builder.service';
import { SynopticPlanInteractionService } from '@connect-services/client/services/synoptic/synoptic-plan-interaction.service';
import { SynopticPlanLayoutService } from '@connect-services/client/services/synoptic/synoptic-plan-layout.service';
import { SynopticPlanRendererService } from '@connect-services/client/services/synoptic/synoptic-plan-renderer.service';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LoadingComponent,
        RessourcesModule,
        SynopticBayPlanEditComponent,
        SynopticPlanEditComponent,
        SynopticPlanHeaderComponent,
    ],
    selector: 'app-synoptic-plan',
    styleUrl: './synoptic-plan.component.scss',
    templateUrl: './synoptic-plan.component.html',
})
export class SynopticPlanComponent implements OnDestroy {
    public readonly cables = computed<Cables>(() => {
        const cassettes = this.synopticPlanInteractionService.cassettes();

        return this.synopticPlanBuilderService.groupByCables(cassettes);
    });
    public close = output<void>();
    public readonly cy = input.required<Cytoscape>();
    public readonly drawerPosition = signal<string>('N/A');
    public readonly drawerSize = signal<string>('N/A');
    public readonly filterOptions = signal<SynopticFilterOption[]>([]);
    public readonly isLoading = input.required<boolean>();
    public readonly planItems = model<SynopticPlanItem[]>([]);
    public readonly selectedNode = input.required<SelectedNode>();
    public updateNode = output<SelectedNode>();

    protected layerId = '';

    protected readonly PlanTypeEnum = PlanTypeEnum;
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly selectSynopticService = inject(SelectSynopticService);
    protected readonly synopticGraphService = inject(SynopticGraphService);
    protected synopticPlanInteractionService = inject(SynopticPlanInteractionService);

    private readonly boxPlanContainer = viewChild<ElementRef>('boxPlanContainer');
    private broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.SYNOPTIC);
    private broadcastEditModeChannel = new BroadcastChannel(BroadcastChannelNameEnum.EDIT_MODE);
    private broadcastHighlightChannel = new BroadcastChannel(BroadcastChannelNameEnum.HIGHLIGHT_SYNOPTIC_NODE);
    private resizeSubscription?: Subscription;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly boxPlanEditModeStateService = inject(BoxPlanEditModeStateService);
    private readonly homeService = inject(HomeService);
    private readonly layerListBloc = inject(LayerListBloc);
    private readonly mapSynopticRedirectionService = inject(MapSynopticRedirectionService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly synopticPlanBuilderService = inject(SynopticPlanBuilderService);
    private readonly synopticPlanLayoutService = inject(SynopticPlanLayoutService);
    private readonly synopticPlanRendererService = inject(SynopticPlanRendererService);
    private readonly synopticService = inject(SynopticService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        window.addEventListener('beforeunload', () => this.resetHighlightChannel());

        effect(() => {
            const planType = this.synopticGraphService.planType();

            if (!planType || !this.planItems()) {
                return;
            }

            this.synopticPlanInteractionService.planType.set(planType);
            this.synopticPlanInteractionService.planItems.set(this.planItems());

            this.selectSynopticService.isSynopticGenerating.set(false);

            const boxContainer = this.boxPlanContainer();

            if (!boxContainer) {
                return;
            }

            untracked(() => this.setPlan(this.planItems() as SynopticBoxPlanItem[], boxContainer.nativeElement));
        });

        effect(() => {
            const boxContainer = this.boxPlanContainer();
            if (!this.synopticPlanInteractionService.isPlanRefreshNeeded() || !boxContainer) {
                return;
            }

            this.buildView(boxContainer.nativeElement);
            this.synopticPlanInteractionService.isPlanRefreshNeeded.set(false);
        });

        effect(() => {
            if (!this.selectedNode()) {
                return;
            }

            this.synopticPlanInteractionService.selectedCassetteKey.set(null);
        });

        effect(async () => {
            const deposit: DepositWithStandard | null = this.selectSynopticService.selectedDeposit();
            const synoptic: SelectedSynoptic | null = this.selectSynopticService.selectedSynoptic();
            const node: SelectedNode = this.selectedNode();
            const layers: Layer[] = this.layerListBloc.value()?.items ?? [];

            if (!node || !deposit || !synoptic) {
                return;
            }

            const synopticItem = synoptic.data.find(
                (item: GraceThdSynopticItem | GenericSynopticItem) =>
                    node.dataNode.id === item.from || node.dataNode.id === item.to,
            );

            if (!synopticItem) {
                return;
            }

            if (!this.homeService.isExternalBoxPlanPage()) {
                this.layerId = await this.getCurrentLayerId(layers, deposit.id, node.dataNode.uuid, synopticItem);
            }

            this.synopticPlanRendererService.initContextMenuOnFiberClick(node, this.layerId);

            await this.handleEntityHighlight(deposit, synopticItem);
        });

        this.openCassettesOnResize();

        this.broadcastEditModeChannel.onmessage = (
            event: MessageEvent<BroadcastChannelEvent<{ isEditModeActive: boolean }>>,
        ): void => {
            if (
                event.data.source !== BroadcastChannelSourceEnum.HOMEPAGE ||
                event.data.additionalData?.isEditModeActive
            ) {
                return;
            }

            this.selectSynopticService.isEditMode.set(false);
            this.boxPlanEditModeStateService.clearEditModeState(this.projectBloc.value()?.id ?? '');
        };
    }

    public ngOnDestroy(): void {
        window.removeEventListener('beforeunload', () => this.resetHighlightChannel());
        document.querySelector('#styleSheetEl')?.remove();

        this.resizeSubscription?.unsubscribe();
        this.broadcastHighlightChannel.close();
    }

    protected closePlan(): void {
        this.close.emit();
    }

    protected async toggleEditMode(isActive: boolean): Promise<void> {
        this.selectSynopticService.isEditMode.set(isActive);

        this.broadcastChannel.postMessage(<BroadcastChannelEvent<boolean>>{
            additionalData: isActive,
            message: BroadcastChannelMessageEnum.SYNOPTIC_EDIT_MODE_ACTIVE,
            source: BroadcastChannelSourceEnum.SYNOPTIC_PLAN,
        });

        if (isActive) {
            return;
        }

        const items = await this.refreshPlanItems();

        if (!items?.length) {
            return;
        }

        this.planItems.set(items);
    }

    private buildEndBanner(rawType: PlanTypeEnum | null): HTMLDivElement {
        const endBanner = document.createElement('div');
        endBanner.setAttribute('class', 'boxPlan__endBanner');
        const endBannerText = document.createElement('p');
        const type = this.translocoService.translate(`synopticPlan.${rawType}`);
        endBannerText.textContent = this.translocoService.translate('synopticPlan.endOfPlan', {
            type: type.toLowerCase(),
        });
        endBanner.append(endBannerText);

        return endBanner;
    }

    private buildView(container: HTMLElement): void {
        this.synopticPlanRendererService.resetPlanHtml();

        const cassettes: Cassettes<SynopticPlanItem> = this.synopticPlanInteractionService.cassettes();

        const rawType: PlanTypeEnum | null = this.synopticGraphService.planType();
        if (rawType === PlanTypeEnum.BAY) {
            const synopticBayPlanItem = Object.values(cassettes)[0][0] as SynopticBayPlanItem;
            this.drawerPosition.set(synopticBayPlanItem.position_tiroir ? synopticBayPlanItem.position_tiroir : 'N/A');
            this.drawerSize.set(synopticBayPlanItem.taille_tiroir ? synopticBayPlanItem.taille_tiroir : 'N/A');
        } else {
            this.synopticPlanInteractionService.cassetteCount.set(`${Object.keys(cassettes).length}`);
        }

        const sortedCassettes = this.synopticPlanBuilderService.sortCassettes(cassettes);
        const cables = this.cables();

        sortedCassettes.forEach((cassetteItems: SynopticPlanItem[]): void => {
            const cassetteCode = cassetteItems[0].cassette;
            if (!cassetteCode) {
                return;
            }

            // Build cable and tubes
            const cableData = cables[cassetteCode];
            const cable = this.synopticPlanRendererService.generateCableAndTubes(
                cassetteCode,
                cableData,
                this.cy(),
                (node) => this.changeNode(node),
                this.selectedNode(),
            );

            const cassette = this.synopticPlanRendererService.generateCassette(
                cassetteCode,
                cassetteItems as SynopticBoxPlanItem[],
            );

            // Build HTML
            // Build view
            const view = document.createElement('div');
            view.setAttribute('id', `boxPlan_view_${cassetteCode}`);
            view.setAttribute('class', 'boxPlan__view');
            view.appendChild(cable.left!);
            view.appendChild(cassette);
            view.appendChild(cable.right!);
            container.appendChild(view);

            // Sort cables by min tube value
            const sides = view.querySelectorAll<HTMLDivElement>('.boxPlan__side');

            sides.forEach((side) => {
                [...Array.from<HTMLElement>(side.children as HTMLCollectionOf<HTMLElement>)]
                    .sort((a, b) => {
                        const aMinTubeValue = a.dataset['mintubevalue'] as unknown as number;
                        const bMinTubeValue = b.dataset['mintubevalue'] as unknown as number;

                        return aMinTubeValue - bMinTubeValue;
                    })
                    .forEach((children) => side.appendChild(children));
            });

            // Collapse down every part
            this.synopticPlanRendererService.hideAllCassettes();
            this.synopticPlanRendererService.toggleCables(cassetteCode, 'none', true);
            this.synopticPlanRendererService.toggleTubes(cassetteCode, 'none', '0', true);
        });

        container.appendChild(this.buildEndBanner(rawType));

        // Needs the timeout or does not run
        setTimeout(() => {
            container.scrollTo(0, 0);
        }, 0);
    }

    private changeNode(node: SelectedNode): void {
        if (!this.homeService.isExternalBoxPlanPage()) {
            this.updateNode.emit(node);

            return;
        }

        this.broadcastChannel.postMessage(<BroadcastChannelEvent<SynopticNodeData>>{
            additionalData: node.dataNode,
            message: BroadcastChannelMessageEnum.MOVE_SYNOPTIC,
            source: BroadcastChannelSourceEnum.SYNOPTIC_PLAN,
        });
    }

    private async getCurrentLayerId(
        layers: Layer[],
        depositId: string,
        entityId: string,
        synopticItem: GraceThdSynopticItem | GenericSynopticItem,
    ): Promise<string> {
        try {
            const entityInformation = await this.mapSynopticRedirectionService.findEntityInLayerService(
                synopticItem,
                entityId,
                depositId,
                LayerStyleTypeEnum.SYMBOL,
            );

            AssertService.isString(entityInformation.layerStyle?.id);

            const layer = layers.find((layer: Layer) => layer.layerStyle === entityInformation.layerStyle!.id);

            return layer?.id ?? '';
        } catch {
            return '';
        }
    }

    private async handleEntityHighlight(
        deposit: DepositWithStandard,
        synopticItem: GraceThdSynopticItem | GenericSynopticItem,
    ): Promise<void> {
        if (this.homeService.isExternalPage()) {
            this.broadcastHighlightChannel.postMessage(<BroadcastChannelEvent<SynopticMapData>>{
                additionalData: {
                    depositId: deposit.id,
                    layerStyleType: LayerStyleTypeEnum.SYMBOL,
                    node: this.selectedNode().dataNode.uuid,
                    synopticItem,
                },
                source: BroadcastChannelSourceEnum.SYNOPTIC_PLAN,
            });

            return;
        }

        await this.mapSynopticRedirectionService.moveMapToEntityLocationFromSynopticNode(
            this.selectedNode().dataNode.uuid,
            deposit.id,
            LayerStyleTypeEnum.SYMBOL,
            synopticItem,
        );
    }

    private openCassettesOnResize(): void {
        this.resizeSubscription = fromEvent(window, 'resize')
            .pipe(debounceTime(150))
            .subscribe(() => {
                const openedCassettes: Cassette[] = this.synopticPlanRendererService.openedCassettes();

                this.synopticPlanRendererService.hideAllCassettes();
                const boxContainer = this.boxPlanContainer();
                if (!boxContainer) {
                    return;
                }
                this.setPlan(this.planItems() as SynopticBoxPlanItem[], boxContainer.nativeElement);

                requestAnimationFrame((): void => {
                    openedCassettes.forEach((cassette: Cassette): void => {
                        const parent: HTMLElement | null = document.querySelector<HTMLElement>(
                            `#boxPlan_view_${cassette.key}`,
                        );
                        const target: HTMLElement | null | undefined =
                            parent?.querySelector<HTMLElement>('.boxPlan__cassette__header i');

                        if (!parent || !target || !cassette.decomposedCassette) {
                            return;
                        }

                        this.synopticPlanRendererService.switchCassetteState(
                            target,
                            parent,
                            cassette.key,
                            cassette.decomposedCassette,
                        );
                    });
                });
            });
    }

    private refreshPlanItems(): Promise<SynopticPlanItem[] | null> {
        const editSessionId = this.projectEditModeService.getEditSession()?.id;
        const deposit = this.selectSynopticService.selectedDeposit();

        if (!deposit) {
            return new Promise((resolve) => resolve(null));
        }

        try {
            switch (this.synopticGraphService.planType()) {
                case PlanTypeEnum.BOX: {
                    const boxId: string = this.selectedNode().dataNode.uuid;

                    return firstValueFrom(
                        this.synopticService.synopticControllerGetBoxPlan({
                            boxId,
                            depositId: deposit.id,
                            editSessionId,
                        }),
                    );
                }
                case PlanTypeEnum.BAY: {
                    const bayId: string = this.selectedNode().dataNode.uuid;

                    return firstValueFrom(
                        this.synopticService.synopticControllerGetBayPlan({
                            bayId,
                            depositId: deposit.id,
                            editSessionId,
                        }),
                    );
                }
                default: {
                    return new Promise((resolve) => resolve(null));
                }
            }
        } catch {
            return new Promise((resolve) => resolve(null));
        }
    }

    private resetHighlightChannel(): void {
        this.broadcastHighlightChannel.postMessage(<BroadcastChannelEvent<SynopticMapData>>{
            message: BroadcastChannelMessageEnum.RESET_HIGHLIGHT_SYNOPTIC,
            source: BroadcastChannelSourceEnum.SYNOPTIC_PLAN,
        });
    }

    private setPlan(result: SynopticBoxPlanItem[], boxContainer: HTMLElement): void {
        if (!result.length) {
            return;
        }

        // Store global datas for the node
        // Create a dedicated stylesheet to the container
        // Remove oldStyleSheet if necessary
        const oldStyleSheet = document.querySelector('#styleSheetEl');
        if (oldStyleSheet) {
            oldStyleSheet.parentNode?.removeChild(oldStyleSheet);
        }

        const styleSheetEl = document.createElement('style');
        styleSheetEl.setAttribute('id', 'styleSheetEl');
        document.head.appendChild(styleSheetEl);
        const stylesheet = styleSheetEl.sheet as CSSStyleSheet;

        // Globals infos
        this.synopticPlanRendererService.jsPlumbInstances = {};

        const cassettes = this.synopticPlanInteractionService.cassettesStore();
        const rawType = this.synopticGraphService.planType();

        if (rawType) {
            this.filterOptions.set(this.synopticPlanBuilderService.fillUpFilter(rawType, cassettes));
        }

        if (rawType === PlanTypeEnum.BAY) {
            // Drawer cassettes
            const drawerIndex = Object.keys(cassettes)[0];
            const drawer = cassettes[drawerIndex] as Drawer;
            this.synopticPlanInteractionService.cassettes.set(drawer.cassettes);

            //Drawer header
            this.synopticPlanInteractionService.drawerValue.set(drawerIndex);
        } else if (rawType === PlanTypeEnum.BOX) {
            this.synopticPlanInteractionService.cassettes.set(cassettes as Cassettes<SynopticBoxPlanItem>);
        }
        this.buildView(boxContainer);

        // Adds global styles
        this.synopticPlanLayoutService.applyStatusClass(stylesheet, this.selectedNode()?.rawNode);
    }
}
